import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Users, Building2, Sliders, Tag, Type, BarChart3, Globe, Mail, Layers, Cpu, Swords, Bot, LineChart, Timer, Gift, Bug } from "lucide-react";
import { AdminErrors } from "@/components/admin/AdminErrors";
import { AdminAutomations } from "@/components/AdminAutomations";
import { AdminInvitations } from "@/components/admin/AdminInvitations";
import { AdminSiteAudit } from "@/components/AdminSiteAudit";
import { AdminCompetitors } from "@/components/AdminCompetitors";
import { AdminAIVisibility } from "@/components/AdminAIVisibility";
import { AdminEmail } from "@/components/AdminEmail";
import { AdminSectors } from "@/components/AdminSectors";
import { AdminAI } from "@/components/AdminAI";
import { AdminPlans } from "@/components/AdminPlans";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { AdminUsers } from "@/components/AdminUsers";
import { AdminActivity } from "@/components/AdminActivity";
import { AdminPurchases } from "@/components/AdminPurchases";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [ests, setEsts] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [texts, setTexts] = useState({});

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/establishments").then((r) => setEsts(r.data));
    api.get("/admin/criteria").then((r) => setCriteria(r.data));
    api.get("/admin/texts").then((r) => setTexts(r.data));
  }, []);

  const saveCriteria = async (c) => {
    await api.put(`/admin/criteria/${c.check_id}`, { weight: Number(c.weight), active: c.active });
    toast.success("Critère mis à jour");
  };
  const saveTexts = async () => {
    await api.put("/admin/texts", texts);
    toast.success("Textes enregistrés");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Administration</h1>
      <p className="text-slate-500 mt-1">Pilotez l'application sans toucher au code.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {stats && [
          { label: "Utilisateurs", value: stats.users, icon: Users },
          { label: "Entreprises", value: stats.companies, icon: Building2 },
          { label: "Établissements", value: stats.establishments, icon: Building2 },
          { label: "Audits", value: stats.audits, icon: BarChart3 },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 border border-slate-200" data-testid={`admin-stat-${i}`}>
            <s.icon className="w-5 h-5 text-blue-600" />
            <div className="font-heading text-2xl font-extrabold mt-2">{s.value}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "analytics"} className="mt-8">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics"><LineChart className="w-4 h-4 mr-1.5" /> Analytics</TabsTrigger>
          <TabsTrigger value="criteria" data-testid="admin-tab-criteria"><Sliders className="w-4 h-4 mr-1.5" /> Critères & pondérations</TabsTrigger>
          <TabsTrigger value="site" data-testid="admin-tab-site"><Globe className="w-4 h-4 mr-1.5" /> Audit du site</TabsTrigger>
          <TabsTrigger value="plans" data-testid="admin-tab-plans"><Tag className="w-4 h-4 mr-1.5" /> Offres & tarifs</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1.5" /> Utilisateurs</TabsTrigger>
          <TabsTrigger value="ests" data-testid="admin-tab-ests"><Building2 className="w-4 h-4 mr-1.5" /> Établissements</TabsTrigger>
          <TabsTrigger value="texts" data-testid="admin-tab-texts"><Type className="w-4 h-4 mr-1.5" /> Textes</TabsTrigger>
          <TabsTrigger value="email" data-testid="admin-tab-email"><Mail className="w-4 h-4 mr-1.5" /> E-mails</TabsTrigger>
          <TabsTrigger value="automations" data-testid="admin-tab-automations"><Timer className="w-4 h-4 mr-1.5" /> Automatisations</TabsTrigger>
          <TabsTrigger value="invitations" data-testid="admin-tab-invitations"><Gift className="w-4 h-4 mr-1.5" /> Invitations & essais</TabsTrigger>
          <TabsTrigger value="sectors" data-testid="admin-tab-sectors"><Layers className="w-4 h-4 mr-1.5" /> Secteurs</TabsTrigger>
          <TabsTrigger value="ai" data-testid="admin-tab-ai"><Cpu className="w-4 h-4 mr-1.5" /> IA & coûts</TabsTrigger>
          <TabsTrigger value="competitors" data-testid="admin-tab-competitors"><Swords className="w-4 h-4 mr-1.5" /> Concurrents</TabsTrigger>
          <TabsTrigger value="aiv" data-testid="admin-tab-aiv"><Bot className="w-4 h-4 mr-1.5" /> Visibilité IA / GEO</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity"><BarChart3 className="w-4 h-4 mr-1.5" /> Comprendre mon activité</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="admin-tab-purchases"><BarChart3 className="w-4 h-4 mr-1.5" /> Achats & Marges</TabsTrigger>
          <TabsTrigger value="errors" data-testid="admin-tab-errors"><Bug className="w-4 h-4 mr-1.5" /> Erreurs</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="mt-4"><AdminAnalytics /></TabsContent>
        <TabsContent value="errors" className="mt-4"><AdminErrors /></TabsContent>

        <TabsContent value="criteria" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Critère</TableHead><TableHead>Pilier</TableHead>
                  <TableHead className="w-28">Poids</TableHead><TableHead className="w-24">Actif</TableHead><TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {criteria.map((c, idx) => (
                  <TableRow key={c.check_id} data-testid={`criteria-row-${c.check_id}`}>
                    <TableCell className="max-w-xs"><span className="text-sm font-medium">{c.label}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{c.pillar}</TableCell>
                    <TableCell>
                      <Input type="number" min={0} max={10} step={0.5} value={c.weight}
                        onChange={(e) => setCriteria((prev) => prev.map((x, i) => i === idx ? { ...x, weight: e.target.value } : x))}
                        data-testid={`criteria-weight-${c.check_id}`} className="h-8 w-20" />
                    </TableCell>
                    <TableCell>
                      <Switch checked={c.active !== false} onCheckedChange={(v) => setCriteria((prev) => prev.map((x, i) => i === idx ? { ...x, active: v } : x))} data-testid={`criteria-active-${c.check_id}`} />
                    </TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => saveCriteria(c)} data-testid={`criteria-save-${c.check_id}`} className="h-8 rounded-lg">OK</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="site" className="mt-4"><AdminSiteAudit /></TabsContent>

        <TabsContent value="plans" className="mt-4"><AdminPlans /></TabsContent>

        <TabsContent value="users" className="mt-4"><AdminUsers /></TabsContent>

        <TabsContent value="ests" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Ville</TableHead><TableHead>Secteur</TableHead><TableHead>Type</TableHead></TableRow></TableHeader>
              <TableBody>
                {ests.map((e) => (
                  <TableRow key={e.id} data-testid={`admin-est-${e.id}`}>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell className="text-slate-500">{e.city}</TableCell>
                    <TableCell className="text-slate-500">{e.sector}</TableCell>
                    <TableCell>{e.is_demo ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">DÉMO</span> : "Réel"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="texts" className="mt-4">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 space-y-4 max-w-2xl" data-testid="admin-texts">
            <div><label className="text-xs text-slate-400">Titre principal (Hero)</label><Textarea value={texts.hero_title || ""} onChange={(e) => setTexts({ ...texts, hero_title: e.target.value })} className="mt-1" data-testid="text-hero-title" /></div>
            <div><label className="text-xs text-slate-400">Sous-titre (Hero)</label><Textarea value={texts.hero_subtitle || ""} onChange={(e) => setTexts({ ...texts, hero_subtitle: e.target.value })} className="mt-1" data-testid="text-hero-subtitle" /></div>
            <div><label className="text-xs text-slate-400">Bouton d'action</label><Input value={texts.hero_cta || ""} onChange={(e) => setTexts({ ...texts, hero_cta: e.target.value })} className="mt-1" data-testid="text-hero-cta" /></div>
            <Button onClick={saveTexts} data-testid="save-texts-button" className="bg-blue-600 hover:bg-blue-700 rounded-xl">Enregistrer les textes</Button>
          </div>
        </TabsContent>

        <TabsContent value="email" className="mt-4"><AdminEmail /></TabsContent>
        <TabsContent value="automations" className="mt-4"><AdminAutomations /></TabsContent>
        <TabsContent value="invitations" className="mt-4"><AdminInvitations /></TabsContent>
        <TabsContent value="sectors" className="mt-4"><AdminSectors /></TabsContent>
        <TabsContent value="ai" className="mt-4"><AdminAI /></TabsContent>
        <TabsContent value="competitors" className="mt-4"><AdminCompetitors /></TabsContent>
        <TabsContent value="aiv" className="mt-4"><AdminAIVisibility /></TabsContent>
        <TabsContent value="activity" className="mt-4"><AdminActivity /></TabsContent>
        <TabsContent value="purchases" className="mt-4"><AdminPurchases /></TabsContent>
      </Tabs>
    </div>
  );
}
